import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { animeList, heroAnimeIds } from "@/mocks/anime";
import { categoryIdFromName } from "@/utils/anime";
import type { Anime } from "@/types/anime";

export default function HeroCarousel() {
  const heroItems: Anime[] = heroAnimeIds
    .map((id) => animeList.find((a) => a.id === id))
    .filter((a): a is Anime => Boolean(a));

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroItems.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [heroItems.length]);

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + heroItems.length) % heroItems.length);
  };

  const current = heroItems[index];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-foreground-950">
      {heroItems.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={item.banner}
            alt={item.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30"></div>

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl">
          <div key={current?.id} className="animate-float-up">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                {current?.status}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-white/15 text-white text-xs backdrop-blur">
                {current?.category}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-white/15 text-white text-xs backdrop-blur">
                {current?.region} · {current?.year}
              </span>
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-medium">
                <i className="ri-star-fill"></i>
                {current?.rating}
              </span>
            </div>

            <h1 className="font-heading font-extrabold text-white text-3xl md:text-5xl leading-tight">
              {current?.title}
            </h1>
            <p className="mt-4 text-white/80 text-sm md:text-base leading-relaxed line-clamp-3 max-w-xl">
              {current?.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current?.id}`}
                className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${categoryIdFromName(current?.category ?? "热血")}`}
                className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-white/10 text-white text-sm font-medium backdrop-blur hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-list-check-2"></i>
                更多同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur hover:bg-black/50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur hover:bg-black/50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroItems.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-8 bg-primary-500" : "w-3 bg-white/50 hover:bg-white/80"}`}
          ></button>
        ))}
      </div>
    </section>
  );
}